import { useEffect } from 'react'
import { Platform, View } from 'react-native'
import { useGridStore } from '../../hooks/use-grid-store'
import { FORMAT_PAINTER_BLUE } from '../../lib/grid-colors'

// Paints the marching-ants outline around the format-painter source range
// while the painter is armed. Cleared automatically after the first
// click/drag apply. Uses FORMAT_PAINTER_BLUE so the ring is visually
// distinct from the clipboard copy ring (which uses the green marching ants).

const PAINTER_STYLE_ID = 'tinycld-calc-painter-ants-style'

// Paintbrush cursor shown while the painter is armed: the classic Excel
// format-painter glyph — a bold white cross (the click target) with a
// small paintbrush to its right. White fills with black outline so it
// reads on any background; hotspot at the cross center (8, 9). Toggled by
// the `calc-format-painter-active` class on <html>, set from Grid's effect.
const PAINTER_CURSOR_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="31" height="19" viewBox="0 0 31 19"><g fill="#fff" stroke="#000" stroke-width="1.5" stroke-linejoin="round"><polygon points="6,2 11,2 11,7 16,7 16,12 11,12 11,17 6,17 6,12 1,12 1,7 6,7"/><polygon points="22,3 29,3 28,8 23,8"/><polygon points="23,8 28,8 27,15 24,15"/></g><g stroke="#000" stroke-width="0.9"><line x1="24.6" y1="9" x2="24.6" y2="14.5"/><line x1="25.7" y1="9" x2="25.7" y2="14.5"/><line x1="26.6" y1="9" x2="26.6" y2="14.5"/></g></svg>`
const PAINTER_CURSOR = `url("data:image/svg+xml,${encodeURIComponent(PAINTER_CURSOR_SVG)}") 8 9, copy`

interface FormatPainterOverlayProps {
    colOffsets: Float64Array
    rowOffsets: Float64Array
}

export function FormatPainterOverlay({ colOffsets, rowOffsets }: FormatPainterOverlayProps) {
    useEffect(() => {
        if (Platform.OS !== 'web') return
        if (document.getElementById(PAINTER_STYLE_ID) != null) return
        const style = document.createElement('style')
        style.id = PAINTER_STYLE_ID
        style.textContent = `
@keyframes tinycld-calc-painter-ants {
    from { background-position: 0 0, 8px 100%, 100% 8px, 0 0; }
    to { background-position: 8px 0, 0 100%, 100% 0, 0 8px; }
}
.tinycld-calc-painter-ants {
    position: absolute;
    pointer-events: none;
    background-image:
        linear-gradient(90deg, ${FORMAT_PAINTER_BLUE} 50%, transparent 50%),
        linear-gradient(90deg, ${FORMAT_PAINTER_BLUE} 50%, transparent 50%),
        linear-gradient(0deg, ${FORMAT_PAINTER_BLUE} 50%, transparent 50%),
        linear-gradient(0deg, ${FORMAT_PAINTER_BLUE} 50%, transparent 50%);
    background-size: 8px 2px, 8px 2px, 2px 8px, 2px 8px;
    background-position: 0 0, 0 100%, 0 0, 100% 0;
    background-repeat: repeat-x, repeat-x, repeat-y, repeat-y;
    animation: tinycld-calc-painter-ants 0.6s linear infinite;
}
html.calc-format-painter-active, html.calc-format-painter-active * {
    cursor: ${PAINTER_CURSOR} !important;
}
`
        document.head.appendChild(style)
    }, [])

    const range = useGridStore(s => s.formatPainterSourceRange)
    if (range == null) return null

    const left = colOffsets[range.startCol - 1] ?? 0
    const right = colOffsets[range.endCol] ?? left
    const width = right - left
    if (width <= 0) return null
    const top = rowOffsets[range.startRow - 1] ?? 0
    const bottom = rowOffsets[range.endRow] ?? top
    const height = bottom - top
    if (height <= 0) return null

    if (Platform.OS === 'web') {
        const webProps = {
            className: 'tinycld-calc-painter-ants',
        } as unknown as Record<string, unknown>
        return (
            <View
                {...webProps}
                pointerEvents="none"
                style={{ position: 'absolute', left, top, width, height }}
            />
        )
    }

    return (
        <View
            pointerEvents="none"
            style={{
                position: 'absolute',
                left,
                top,
                width,
                height,
                borderWidth: 2,
                borderStyle: 'dashed',
                borderColor: FORMAT_PAINTER_BLUE,
            }}
        />
    )
}
